import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/products/shared/state/auth-context';
import { useRBAC } from '@/products/shared/utils/rbac';
import { isLimitedComplianceRole } from '@/products/shared/utils/feature-access';
import { COMPLIANCE_DISCLAIMER_VERSION } from '../constants/disclaimer';
import {
  acceptComplianceDisclaimer,
  fetchComplianceDisclaimerAcceptance,
} from '../services/complianceDisclaimer';

export const complianceDisclaimerQueryKey = (
  tenantId: number | null,
  userId: string | null | undefined,
  version: string = COMPLIANCE_DISCLAIMER_VERSION,
) => ['compliance', 'disclaimer-acceptance', tenantId, userId, version] as const;

export const useComplianceDisclaimerAcceptance = () => {
  const { tenantId, user } = useAuth();
  const { role, loading: roleLoading } = useRBAC();
  const queryClient = useQueryClient();
  const userId = user?.id ?? null;
  const isManager = isLimitedComplianceRole(role);
  const queryEnabled = isManager && Boolean(tenantId && userId);

  const query = useQuery({
    queryKey: complianceDisclaimerQueryKey(tenantId, userId),
    enabled: queryEnabled,
    queryFn: async () => {
      if (!tenantId || !userId) {
        return null;
      }
      return fetchComplianceDisclaimerAcceptance(tenantId, userId);
    },
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      if (!tenantId || !userId) {
        throw new Error('A valid tenant and user are required to accept the disclaimer');
      }
      return acceptComplianceDisclaimer(tenantId, userId);
    },
    onSuccess: (row) => {
      queryClient.setQueryData(
        complianceDisclaimerQueryKey(tenantId, userId),
        row,
      );
    },
  });

  const hasAccepted = Boolean(query.data);

  return {
    isManager,
    roleLoading,
    /** True while role or acceptance status is still loading for a user. */
    isLoading: roleLoading || (queryEnabled && query.isLoading),
    hasAccepted,
    error: query.error ?? acceptMutation.error ?? null,
    accept: acceptMutation.mutateAsync,
    isAccepting: acceptMutation.isPending,
  };
};
